import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './CriterionFeedbackCard.module.css';

export interface CriterionScore {
  criterion: string;
  met: boolean;
  comment: string;
}

interface CriterionFeedbackCardProps {
  criteria: CriterionScore[];
  questionNumber?: number;
  title?: string;
}

export const CriterionFeedbackCard: React.FC<CriterionFeedbackCardProps> = ({
  criteria,
  questionNumber,
  title,
}) => {
  const { t } = useTranslation();
  const displayTitle = title || t('exam.criterionFeedback.title');

  if (!criteria || criteria.length === 0) {
    return (
      <div className={styles.container}>
        <p className={styles.empty}>{t('exam.criterionFeedback.empty')}</p>
      </div>
    );
  }

  const metCount = criteria.filter((c) => c.met).length;
  const totalCount = criteria.length;
  const percentage = Math.round((metCount / totalCount) * 100);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h3 className={styles.title}>
          {questionNumber ? `${t('exam.criterionFeedback.question', { number: questionNumber })}: ` : ''}{displayTitle}
        </h3>
        <div className={styles.progressBadge}>
          <span className={styles.progressText}>
            {metCount}/{totalCount} ({percentage}%)
          </span>
        </div>
      </div>

      <div className={styles.criteriaList}>
        {criteria.map((criterion, index) => (
          <div
            key={index}
            className={`${styles.criterionItem} ${criterion.met ? styles.criterionMet : styles.criterionNotMet}`}
          >
            <div className={styles.criterionHeader}>
              <span className={styles.criterionStatus}>
                {criterion.met ? (
                  <span className={styles.iconMet}>✓</span>
                ) : (
                  <span className={styles.iconNotMet}>✗</span>
                )}
              </span>
              <span className={styles.criterionName}>{criterion.criterion}</span>
            </div>
            {criterion.comment && (
              <p className={styles.criterionComment}>{criterion.comment}</p>
            )}
          </div>
        ))}
      </div>

      <div className={styles.summary}>
        {percentage >= 80 ? (
          <p className={styles.summaryGood}>
            {t('exam.criterionFeedback.summaryGood')}
          </p>
        ) : percentage >= 60 ? (
          <p className={styles.summaryOk}>
            {t('exam.criterionFeedback.summaryOk')}
          </p>
        ) : (
          <p className={styles.summaryPoor}>
            {t('exam.criterionFeedback.summaryPoor')}
          </p>
        )}
      </div>
    </div>
  );
};
